// Generar el PDF de la familia de formatos. Sin aprobación previa (Diego), pero cada aviso rojo sin resolver se
// confirma AQUÍ con una nota que queda en el historial y en la versión. Al terminar: portada JPG (para WhatsApp),
// PDF, descargar o compartir, y marcar "Enviada".
import { pdfDesactualizado } from '../../logica/pdf-al-dia.ts';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { CircleCheck, CircleX, Download, FileDown, MessageCircle, Pencil, Send, Share2, TriangleAlert } from 'lucide-react';
import { hoyEnHermosillo, msiPromoSeguroPorContado, MSI_TOPE_LISTA_CON_IVA, NO_SE_PUEDE_COTIZAR_LEY, avisosArribaDeLaLey, faltaTamanoAmpliacion, loQueCobraElPdf, pdfDetenidoPor, CODIGOS_QUE_DETIENEN_EL_PDF, opcionesArribaDeLaLey, CODIGOS_QUE_NO_SE_CONFIRMAN, CODIGOS_QUE_SOLO_CONFIRMA_DIEGO, aprobacionVigente, avisosQueApruebaDiego, fechaHoraCorta, firmaPrecios, formatoPara, motivoNoAutoriza, pdfTodaviaNoSale, rojosPendientes, soloDescuentosQueAutorizanInternos } from '@cotizador/compartido';
import type { ResumenVersion } from '@cotizador/compartido';
import { api } from '../../api/consultas.ts';
import { ErrorDeApi, bajarArchivo } from '../../api/cliente.ts';
import { usarUsuario } from '../../sesion.tsx';
import { Enlace, navegar } from '../../ruteo.tsx';
import { enlaceWhatsApp, leerNuevaVersion, marcarNuevaVersion, mensajeWhatsApp, textoPromoWhatsApp } from '../../logica/nueva-version.ts';
import { esPdfDeVariosSitios } from '../../logica/sitios.ts';
import { Boton, CampoArea, CampoTexto, ErrorEnLinea, Seccion } from '../../componentes/ui.tsx';
import { descargarComo, usarUrlArchivo } from '../../componentes/archivos.tsx';
import { ETIQUETA_FORMATO, ETIQUETA_OPCION, dinero, kwp } from '../../logica/etiquetas.ts';
import { sinPedirOk } from '../../logica/sin-pedir-ok.ts';
import { BandaAvisos, ETIQUETA_ORIGEN } from '../../componentes/avisos.tsx';
import { usarCotizacion, usarPuedeAqui } from './contexto.tsx';
import { usarPendienteAmpliacion } from '../../logica/pendientes.ts';
import { plantillaPiezas } from '../../logica/ampliacion.ts';

function Revision({ ok, aviso, children }: { ok: boolean; aviso?: boolean; children: ReactNode }) {
  return (
    <li className={['lista-salida__item', ok ? 'es-ok' : aviso ? 'es-aviso' : 'es-mal'].join(' ')}>
      {ok ? <CircleCheck aria-hidden /> : aviso ? <TriangleAlert aria-hidden /> : <CircleX aria-hidden />}
      <span>{children}</span>
    </li>
  );
}

function ejemploNota(codigo: string): string {
  if (codigo === 'FALTAN_RECIBOS' || codigo === 'HISTORIAL_INCOMPLETO') return 'Ej. El cliente sabe que faltan recibos; se ajusta con la visita.';
  if (codigo === 'PIERDE_DINERO' || codigo === 'MARGEN_BAJO' || codigo === 'FIJOS_ENCARECEN') return 'Ej. Diego autorizó este precio por volumen.';
  if (codigo.startsWith('RECIBO') || codigo === 'DATO_DUDOSO') return 'Ej. Confirmé el dato contra el recibo en papel.';
  return 'Qué hiciste o por qué sigue así.';
}

type Nav = Navigator & { canShare?: (d: ShareData) => boolean };

/** Baja un Blob ya en memoria con su nombre (sin red: sirve dentro del mismo toque). */
function guardarBlob(archivo: File): void {
  const url = URL.createObjectURL(archivo);
  const a = document.createElement('a');
  a.href = url;
  a.download = archivo.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Hallazgo del 10-oct: con red lenta, `share()` después de un `await` de descarga pierde el toque ("Must be handling a
 *  user gesture") y salía en inglés. Los archivos se bajan al abrir la tarjeta y se comparten AL MOMENTO del toque. Si el
 *  teléfono no deja compartir archivos, se descargan y se dice en español. */
async function compartirArchivos(archivos: File[], texto: string): Promise<'compartido' | 'descargado'> {
  const nav = navigator as Nav;
  if (nav.share && nav.canShare?.({ files: archivos })) {
    try {
      await nav.share({ files: archivos, text: texto });
      return 'compartido';
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return 'compartido';
      // NotAllowedError (se venció el toque) u otro: se descarga y se dice.
    }
  }
  for (const a of archivos) guardarBlob(a);
  return 'descargado';
}

export function TarjetaVersion({ v, destacada }: { v: ResumenVersion; destacada?: boolean }) {
  const { cot, detalle, ejecutar } = usarCotizacion();
  const { veCostos } = usarUsuario();
  const puede = usarPuedeAqui();
  const portada = usarUrlArchivo(destacada && v.pdf?.archivos.portada ? v.pdf.archivos.portada : null);
  const [error, setError] = useState<unknown>(null);
  const [marcando, setMarcando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [listos, setListos] = useState<{ pdf: File | null; portada: File | null }>({ pdf: null, portada: null });
  const pdf = v.pdf;
  const nombrePdf = pdf ? `${pdf.folio_pdf} ${cot.cliente_nombre}.pdf` : '';
  // z48-6 (Diego, 28-oct): mensaje listo, corto y de ventas (logica/nueva-version.ts → mensajeWhatsApp). Sin enlace público al PDF:
  // el cotizador no tiene uno (son datos del cliente); el PDF se adjunta en el chat.
  // la promo es la que imprimió ESTE PDF (v.promo, de su versión), nunca la del borrador de hoy: quitarla y volver a ponerla después
  // del 17-nov la deja como fin de año con el mismo precio, y el mensaje contradecía al PDF de Buen Fin que acompaña
  // los 18 MSI de la promo van en el PDF de casa; el mensaje los dice solo si su contado asegura que la lista cabe en el tope (3-nov)
  const con18 = pdf?.formato === 'residencial' && v.contado_recomendada !== null && msiPromoSeguroPorContado(v.contado_recomendada);
  const texto = pdf ? mensajeWhatsApp({ promo: v.promo ? textoPromoWhatsApp(v.promo, pdf.generado_en, hoyEnHermosillo(), con18) : null, cliente: cot.cliente_nombre, folio: pdf.folio_pdf, version: v.numero, contado: v.contado_recomendada, kwp: v.kwp_recomendada, asesor: detalle.vendedor_nombre ?? null, todoEnUno: esPdfDeVariosSitios(pdf.formato) }) : '';
  // Se bajan AL ABRIR la tarjeta (no al tocar): así compartir no espera la red y no pierde el toque.
  useEffect(() => {
    if (!destacada || !pdf) return;
    let vivo = true;
    const bajar = async (id: string | null | undefined, nombre: string, tipo: string) => (id ? new File([await bajarArchivo(id)], nombre, { type: tipo }) : null);
    Promise.all([bajar(pdf.archivos.pdf, nombrePdf, 'application/pdf'), bajar(pdf.archivos.portada, `${pdf.folio_pdf}-portada.jpg`, 'image/jpeg')])
      .then(([p, j]) => vivo && setListos({ pdf: p, portada: j }))
      .catch(() => undefined); // si falla, el botón dice "preparando" y Descargar sigue funcionando
    return () => {
      vivo = false;
    };
  }, [destacada, pdf, nombrePdf]);
  if (!pdf) return null;
  const accion = async (f: () => Promise<unknown>) => {
    setError(null);
    setAviso(null);
    try {
      const r = await f();
      if (r === 'descargado') setAviso('Este teléfono no deja compartir archivos desde aquí: se descargaron (portada y PDF). Ábrelos desde Descargas y mándalos por WhatsApp.');
    } catch (e) {
      setError(e);
    }
  };
  const marcarEnviada = async () => {
    setMarcando(true);
    setError(null);
    try {
      await ejecutar((c) => api.actualizarCrm(c.id, { revision: c.revision, estado: 'enviada' }));
    } catch (e) {
      setError(e);
    } finally {
      setMarcando(false);
    }
  };
  const paraWhatsApp = [listos.portada, listos.pdf].filter((x): x is File => x !== null);
  // En la computadora: abre el chat del cliente con el mensaje (dentro del mismo clic, para que el navegador no lo bloquee) y
  // descarga el PDF ya bajado para adjuntarlo.
  const whatsappConMensaje = () => {
    setError(null);
    window.open(enlaceWhatsApp(cot.borrador.cuestionario.cliente.telefono, texto), '_blank', 'noopener,noreferrer');
    if (listos.pdf) guardarBlob(listos.pdf);
    setAviso(`Se abrió WhatsApp con el mensaje y se descargó «${nombrePdf}»: adjúntalo en el chat (arrástralo o con el clip) y mándalo.`);
  };
  const hacerCambios = () => {
    marcarNuevaVersion(cot.id, v.numero);
    navegar(`/cotizaciones/${cot.id}/${cot.borrador.cuestionario.tipo === 'ampliacion_baterias' ? 'ampliacion' : 'opciones'}`);
  };
  const preparado = destacada ? listos.pdf !== null : true;
  return (
    <div className={['version', destacada ? 'version--destacada' : ''].join(' ')}>
      {destacada && pdf.archivos.portada && (
        <div className="version__portada">{portada.url ? <img src={portada.url} alt={`Portada de ${pdf.folio_pdf}`} /> : <span className="version__portada-vacia" />}</div>
      )}
      <div className="version__cuerpo">
        <p className="version__titulo">
          <span className="folio">{pdf.folio_pdf}</span> · versión {v.numero}
        </p>
        <p className="version__meta">
          {fechaHoraCorta(pdf.generado_en)} · formato {ETIQUETA_FORMATO[pdf.formato]} · {v.motivo}
          {v.contado_recomendada !== null && !esPdfDeVariosSitios(pdf.formato) && ` · ${dinero(v.contado_recomendada)} contado${v.kwp_recomendada !== null ? ` (${kwp(v.kwp_recomendada)})` : ''}`}
        </p>
        {pdf.avisos_rojos_confirmados.length > 0 && (
          <p className="version__confirmados">
            {pdf.avisos_rojos_confirmados.length === 1 ? '1 aviso rojo confirmado' : `${pdf.avisos_rojos_confirmados.length} avisos rojos confirmados`}: {pdf.avisos_rojos_confirmados.map((a) => `“${a.nota}”`).join(' · ')}
          </p>
        )}
        <div className="fila-botones">
          {destacada && (
            <Boton chico variante="principal" icono={<MessageCircle aria-hidden />} disabled={!preparado} onClick={whatsappConMensaje}>
              WhatsApp con mensaje listo
            </Boton>
          )}
          {destacada ? (
            <Boton
              chico
              variante="secundario"
              icono={<Send aria-hidden />}
              disabled={!preparado}
              onClick={() => void accion(() => compartirArchivos(paraWhatsApp, texto))}
            >
              {preparado ? 'Mandar por WhatsApp' : 'Preparando el PDF…'}
            </Boton>
          ) : (
            <Boton chico variante="secundario" icono={<Share2 aria-hidden />} onClick={() => void accion(async () => compartirArchivos([new File([await bajarArchivo(pdf.archivos.pdf)], nombrePdf, { type: 'application/pdf' })], texto))}>
              Compartir PDF
            </Boton>
          )}
          <Boton chico variante="fantasma" icono={<Download aria-hidden />} onClick={() => void accion(() => descargarComo(pdf.archivos.pdf, nombrePdf))}>
            Descargar PDF
          </Boton>
          {pdf.archivos.anexo && (
            <Boton chico variante="fantasma" icono={<Download aria-hidden />} onClick={() => void accion(() => descargarComo(pdf.archivos.anexo!, `${pdf.folio_pdf} ${cot.cliente_nombre} anexo técnico.pdf`))}>
              Anexo técnico
            </Boton>
          )}
          {destacada && puede('editar_cotizaciones') && (
            <Boton chico variante="secundario" icono={<Pencil aria-hidden />} onClick={hacerCambios}>
              Hacer cambios (nueva versión)
            </Boton>
          )}
          {destacada && puede('editar_cotizaciones') && cot.estado === 'borrador' && (
            <Boton chico variante="noche" icono={<CircleCheck aria-hidden />} cargando={marcando} onClick={() => void marcarEnviada()}>
              Marcar como enviada
            </Boton>
          )}
        </div>
        {destacada && (
          <p className="nota-suave">
            En la computadora, «WhatsApp con mensaje listo» abre {cot.borrador.cuestionario.cliente.telefono ? 'el chat del cliente' : 'WhatsApp'} con el mensaje y descarga el PDF para adjuntarlo. En el celular, «Mandar por
            WhatsApp» manda la portada y el PDF juntos.
          </p>
        )}
        {pdf.avisos_generador.length > 0 && destacada && veCostos && (
          <details className="plegable">
            <summary>Avisos del generador para Diego ({pdf.avisos_generador.length})</summary>
            <ul className="lista-simple">
              {pdf.avisos_generador.map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
          </details>
        )}
        {aviso && <p className="nota-suave">{aviso}</p>}
        <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
      </div>
    </div>
  );
}

/** ¿Lo único que pide el OK es un renglón sin monto (COSTO_SIN_MONTO)? Entonces el aviso habla del monto, no del precio. */
function soloFaltaMonto(piden: readonly { codigo: string }[]): boolean {
  return piden.length > 0 && piden.every((a) => a.codigo === 'COSTO_SIN_MONTO');
}

/** ADENDA A0b: descuento mayor al tope, debajo del piso o del margen mínimo, o un renglón sin monto → el OK de Diego.
 *  El vendedor lo pide (queda en el historial y se le abre WhatsApp con el resumen y la liga); Diego aprueba o rechaza
 *  los precios que está viendo desde esta misma sección. El vendedor nunca ve el margen (11-oct). */
function OkDeDiego() {
  const { cot, ejecutar } = usarCotizacion();
  const { usuario, puede: puedeRol } = usarUsuario();
  const puede = usarPuedeAqui();
  const b = cot.borrador;
  const piden = avisosQueApruebaDiego(b.avisos);
  const vigente = aprobacionVigente(b);
  const a = b.aprobacion ?? null;
  const misma = !!a && !!b.resultado && a.firma === firmaPrecios(b.resultado);
  const esDirector = puedeRol('aprobar_precios');
  // Diego, 11/12-oct: Miguel y Juan Carlos también autorizan un DESCUENTO (no el suyo); debajo del piso, solo Diego.
  const descuento = soloDescuentosQueAutorizanInternos(b.avisos);
  // Pendiente del 13-oct: si lo único que pide el OK es un renglón SIN monto (COSTO_SIN_MONTO), no es «precio abajo de lo
  // recomendado»: falta el monto de un costo adicional.
  const soloSinMonto = soloFaltaMonto(piden);
  const puedeAutorizar = motivoNoAutoriza({ id: usuario.id, rol: usuario.rol, activo: true }, cot, b.avisos) === null;
  // Diego, junta del 13-oct: ya nada detiene el PDF salvo el límite de la ley (0.7 MW), que no se aprueba con ningún OK.
  const bloqueado: number = 0;
  const [nota, setNota] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [whatsapp, setWhatsapp] = useState<{ url: string | null; texto: string; opciones: { nombre: string; url: string }[] } | null>(null);
  if (!piden.length || !b.resultado) return null;
  const pedir = async () => {
    setOcupado(true);
    setError(null);
    try {
      const r = await ejecutar((c) => api.pedirAprobacion(c.id, c.revision, nota.trim() || null));
      setWhatsapp({ url: r.whatsapp_url, texto: r.texto, opciones: r.whatsapp_opciones ?? (r.whatsapp_url ? [{ nombre: 'Diego', url: r.whatsapp_url }] : []) });
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  };
  const resolver = async (aprobar: boolean) => {
    setOcupado(true);
    setError(null);
    try {
      await ejecutar((c) => api.resolverAprobacion(c.id, c.revision, aprobar, nota.trim() || null));
      setNota('');
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  };
  // A quien no autoriza, el precio bajo es sugerencia: sin la cola «necesita el OK» (Diego, junta del 13-oct).
  const motivos = [...new Set(piden.map((x) => (esDirector || puedeAutorizar ? x.mensaje : sinPedirOk(x.mensaje))))];
  return (
    <section className={['ok-diego', vigente ? 'ok-diego--aprobado' : misma && a?.estado === 'rechazada' ? 'ok-diego--rechazado' : ''].join(' ')} aria-labelledby="ok-diego-titulo">
      <p className="ok-diego__titulo" id="ok-diego-titulo">
        {vigente ? <CircleCheck aria-hidden /> : <TriangleAlert aria-hidden />}
        {vigente
          ? 'Precios aprobados'
          : esDirector
            ? soloSinMonto
              ? 'Falta el monto de un costo adicional: pide tu OK'
              : 'Este precio pide tu OK'
            : puedeAutorizar
              ? 'Este descuento pide un OK: lo puedes autorizar tú'
              : descuento
                ? 'Sugerencia: este descuento pasa de tu tope (el PDF sale igual y Diego recibe el aviso)'
                : soloSinMonto
                  ? 'Sugerencia: falta el monto de un costo adicional (el PDF sale igual y Diego recibe el aviso). Pídele el monto a Diego.'
                  : 'Sugerencia: este precio quedó abajo de lo recomendado (el PDF sale igual y Diego recibe el aviso). Si puedes, llévalo a verde en Opciones.'}
      </p>
      <ul className="lista-simple">
        {motivos.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>
      {vigente && <p className="nota-suave">Aprobado el {fechaHoraCorta(vigente.resuelta_en ?? vigente.pedida_en)}{vigente.nota ? `: «${vigente.nota}»` : ''}. Ya puedes generar el PDF.</p>}
      {!vigente && misma && a?.estado === 'pendiente' && <p className="nota-suave">Se pidió el OK el {fechaHoraCorta(a.pedida_en)}. Todavía no contestan.</p>}
      {!vigente && misma && a?.estado === 'rechazada' && <p className="texto-rojo">No lo aprobaron{a.nota ? `: «${a.nota}»` : ''}. Cambia el precio (en Opciones) o pide el OK otra vez.</p>}
      {!vigente && bloqueado > 0 && (
        <p className="nota-suave">
          Primero arregla {bloqueado === 1 ? 'el aviso rojo que no se confirma' : `los ${bloqueado} avisos rojos que no se confirman`} con nota (abajo, en «Generar el PDF»): con {bloqueado === 1 ? 'él' : 'ellos'} el PDF no sale, ni con el OK. Después se pide el OK.
        </p>
      )}
      {!vigente && bloqueado === 0 && puedeAutorizar && (
        <div className="pila pila--chica">
          <CampoTexto etiqueta="Tu nota (la ve el vendedor)" maxLength={500} value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Ej. va por volumen; o: súbelo a la tabla" />
          <div className="fila-botones">
            <Boton variante="principal" icono={<CircleCheck aria-hidden />} cargando={ocupado} onClick={() => void resolver(true)}>
              Aprobar estos precios
            </Boton>
            <Boton variante="peligro" icono={<CircleX aria-hidden />} disabled={ocupado || nota.trim().length < 3} onClick={() => void resolver(false)}>
              Rechazar
            </Boton>
          </div>
          {esDirector && <p className="nota-suave">También puedes generar el PDF tú mismo: tu nota en cada aviso rojo cuenta como tu OK.</p>}
        </div>
      )}
      {!vigente && bloqueado === 0 && !puedeAutorizar && puede('editar_cotizaciones') && (
        <div className="pila pila--chica">
          {!whatsapp ? (
            <>
              <CampoTexto etiqueta={descuento ? 'Nota para quien lo autoriza (opcional)' : 'Nota para Diego (opcional)'} maxLength={500} value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Ej. el cliente compara con otra marca" />
              <Boton variante="principal" icono={<Send aria-hidden />} cargando={ocupado} onClick={() => void pedir()}>
                {misma && a?.estado === 'pendiente' ? 'Pedir el OK otra vez' : descuento ? 'Pedir OK' : 'Pedir OK a Diego'}
              </Boton>
            </>
          ) : whatsapp.opciones.length ? (
            <div className="pila pila--chica">
              {whatsapp.opciones.map((o) => (
                <a key={o.url} className="boton boton--principal boton--envuelve" href={o.url} target="_blank" rel="noreferrer">
                  <Send aria-hidden />
                  <span>Mandarle el resumen por WhatsApp a {o.nombre}</span>
                </a>
              ))}
            </div>
          ) : (
            <>
              <p className="nota-suave">Nadie que pueda autorizarlo tiene WhatsApp en su usuario: copia este mensaje y mándaselo a Diego.</p>
              <pre className="texto-copiable">{whatsapp.texto}</pre>
            </>
          )}
        </div>
      )}
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
    </section>
  );
}

export function SeccionPdf() {
  const { cot, detalle, ejecutar, recargar } = usarCotizacion();
  const { config, usuario, puede: puedeRol } = usarUsuario();
  const puede = usarPuedeAqui();
  const b = cot.borrador;
  const esDirector = puedeRol('aprobar_precios');
  // Los de precio (OK de Diego) no se confirman con nota del vendedor: van en el recuadro de arriba.
  const deAprobacion = new Set(avisosQueApruebaDiego(b.avisos).map((a) => a.id));
  const aprobado = aprobacionVigente(b);
  // Los que no se confirman con nota (un recibo quitado que sigue en la lectura): hay que arreglar el dato.
  // OPTIMUS (Diego, 12-oct noche): un precio que no es el que le toca al vendedor solo lo mantiene Diego con su nota; los
  // demás lo arreglan con «Aplicar el precio…» en Opciones (el servidor rechaza la nota de cualquier otro).
  // Diego, junta del 13-oct (ADENDA «DIEGO EN LA JUNTA», a): ningún aviso detiene el PDF salvo el precio de Optimus sin aplicar.
  const seArregla = (a: { codigo: (typeof CODIGOS_QUE_NO_SE_CONFIRMAN)[number] }) => !esDirector && CODIGOS_QUE_SOLO_CONFIRMA_DIEGO.includes(a.codigo);
  const porArreglar = rojosPendientes(b.avisos).filter(seArregla);
  // Diego, 13-oct (22:30): arriba de 0.7 MW no se puede: el PDF no sale (la única excepción a «nada detiene el PDF»).
  const arribaLey = avisosArribaDeLaLey(rojosPendientes(b.avisos));
  // …y, como el servidor, medido en las opciones mismas con lo que ya tiene (aunque el aviso no esté).
  const arribaLeyMedida = b.resultado ? opcionesArribaDeLaLey(b.resultado.opciones, b.cuestionario) : [];
  // Diego, 24-oct: los viáticos que faltan no son sugerencia: van en la lista de salida en rojo y el botón no se prende.
  // (y E7: un aislado sin sus equipos tampoco saca PDF: mismo trato que los viáticos)
  const sinViaticos = pdfDetenidoPor(b.avisos);
  const rojos = rojosPendientes(b.avisos).filter((a) => (esDirector || !deAprobacion.has(a.id)) && !seArregla(a) && !arribaLey.includes(a) && !CODIGOS_QUE_DETIENEN_EL_PDF.includes(a.codigo));
  const faltaOk = !esDirector && deAprobacion.size > 0 && !aprobado && rojosPendientes(b.avisos).some((a) => deAprobacion.has(a.id));
  const formato = formatoPara(b.cuestionario, b.resultado?.tarifa ?? null);
  const formatoListo = config.pdf.formatos.includes(formato);
  // Verificación adversarial del 14-oct: híbrido y aislado todavía no imprimen (el servidor contesta 422 con este texto). Antes
  // la lista salía toda en verde y el botón prendido; ahora lo dice en rojo y el botón no se prende para un PDF que no sale.
  const sinPdf = pdfTodaviaNoSale(b.cuestionario);
  // Ampliación sin el tamaño de lo que ya tiene: el límite de la ley no se puede revisar y el PDF no sale (como el servidor).
  const faltaTamano = faltaTamanoAmpliacion(b.cuestionario);
  // Ampliación de baterías (Diego, 27-oct-2026): sin motor solar ni opciones; su precio es `calculo_piezas`, no lleva OK de Diego
  // y lo que la detiene son sus propios rojos (falta un dato, la noche de la hoja 2, los viáticos fuera de Hermosillo).
  const esAmpliacion = b.cuestionario.tipo === 'ampliacion_baterias';
  const calculoPiezas = esAmpliacion ? (b.calculo_piezas ?? null) : null;
  const detienenAmpliacion = esAmpliacion ? rojosPendientes(b.avisos).filter((a) => CODIGOS_QUE_DETIENEN_EL_PDF.includes(a.codigo)) : [];
  const sinPrecio = esAmpliacion ? !calculoPiezas : !b.resultado;
  // Verificación de ventas del 27-oct: lo tecleado en «Ampliación» y sin guardar no va en el PDF; hasta guardarlo, no se ofrece.
  const pendiente = usarPendienteAmpliacion(cot.id, b.piezas ?? null);
  const sinGuardar = esAmpliacion && pendiente !== null;
  // Lo que la plantilla conectada sabe hacer (el servidor lo mide y lo dice en sus avisos «pdf»): sin englobar el precio no sale;
  // sin la hoja 2, sale de una hoja.
  const plantilla = esAmpliacion ? plantillaPiezas(b.avisos) : null;
  const noEngloba = plantilla !== null && !plantilla.sin_desglose;
  // z48-7: con «Hacer cambios (nueva versión)» el motivo ya viene escrito.
  const [motivo, setMotivo] = useState(cot.version_actual === 0 ? 'Primera propuesta' : leerNuevaVersion(cot.id) !== null ? `Cambios a la versión ${cot.version_actual}` : '');
  const [notas, setNotas] = useState<Record<string, string>>({});
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [nueva, setNueva] = useState<ResumenVersion | null>(null);
  const conPdf = useMemo(() => detalle.versiones.filter((v) => v.pdf).sort((a, b2) => b2.numero - a.numero), [detalle.versiones]);
  const ultima = nueva ?? conPdf[0] ?? null;
  const anteriores = conPdf.filter((v) => v.numero !== ultima?.numero);
  // El último PDF es de antes del último cálculo (se recalculó después): sus números ya no son los de la cotización.
  const vieja = !nueva && pdfDesactualizado(detalle);
  const rec = b.resultado?.opciones.find((o) => o.id === b.resultado?.recomendada) ?? null;
  // Verificación de hoy87 (vendedor): la lista de salida decía en un aislado «recomendada exacta ($71,200 contado)», el precio de SOLO
  // los paneles, junto al total de $165,300 que cobra el PDF. Ahora dice lo que cobra el PDF (loQueCobraElPdf); sin él, el de la opción.
  const cobra = b.resultado ? (loQueCobraElPdf(b).contado ?? rec?.precio.contado ?? null) : null;
  const vendedorEsYo = cot.vendedor_id === usuario.id;
  // La tarjeta del asesor en la plantilla del PDF: el servidor la revisa contra la plantilla (aviso PDF_ASESOR_NO_REGISTRADO)
  // para CUALQUIER vendedor (el director genera por otros); si no hay aviso y el generador está, la tarjeta existe.
  const avisoAsesor = b.avisos.find((a) => a.codigo === 'PDF_ASESOR_NO_REGISTRADO') ?? null;
  // 13-oct: un aviso «info» (tarjeta encontrada por correo o nombre, o la de respaldo) NO frena el PDF: va en verde.
  const asesorListo = avisoAsesor ? avisoAsesor.nivel === 'info' : config.pdf.disponible ? true : null;

  async function generar() {
    setGenerando(true);
    setError(null);
    try {
      const r = await ejecutar((c) =>
        api.generarPdf(
          c.id,
          c.revision,
          motivo.trim().length >= 2 ? motivo.trim() : 'Sin motivo',
          rojosPendientes(c.borrador.avisos)
            .filter((a) => (notas[a.id] ?? '').trim().length >= 3)
            .map((a) => ({ aviso_id: a.id, nota: (notas[a.id] ?? '').trim() })),
        ),
      );
      setNueva(r.version);
      setNotas({});
      setMotivo('');
    } catch (e) {
      setError(e);
      // Si aparecieron rojos nuevos (alguien recalculó), se recarga para enseñarlos.
      if (e instanceof ErrorDeApi && ['AVISOS_ROJOS_SIN_CONFIRMAR', 'PRECIOS_CAMBIARON', 'APROBACION_PENDIENTE', 'PDF_FALLO'].includes(e.codigo)) await recargar().catch(() => undefined);
    } finally {
      setGenerando(false);
    }
  }

  return (
    <div className="pila pila--grande">
      <OkDeDiego />
      {puede('editar_cotizaciones') && rojos.length > 0 && (
        <div className="confirmar-rojos">
          <p className="confirmar-rojos__titulo">Sugerencias antes de mandarla</p>
          <p className="nota-suave">No detienen el PDF. Si quieres, deja una nota (opcional){esDirector ? ': en los de precio cuenta como tu OK.' : ' y Diego la ve.'}</p>
          <ol className="confirmar-rojos__lista">
            {rojos.map((a) => (
              <li key={a.id} className="confirmar-rojos__item">
                <p className="confirmar-rojos__mensaje">{a.mensaje}</p>
                <p className="aviso__meta">
                  <span>{ETIQUETA_ORIGEN[a.origen]}</span>
                  {a.opcion && <span>Opción {ETIQUETA_OPCION[a.opcion].toLowerCase()}</span>}
                </p>
                <CampoArea
                  etiqueta="Tu nota (opcional)"
                  rows={2}
                  maxLength={500}
                  placeholder={ejemploNota(a.codigo)}
                  value={notas[a.id] ?? ''}
                  onChange={(e) => setNotas({ ...notas, [a.id]: e.target.value })}
                />
              </li>
            ))}
          </ol>
        </div>
      )}

      {!puede('editar_cotizaciones') && <BandaAvisos avisos={b.avisos} soloRojos />}
      {ultima && !vieja && (
        <Seccion titulo={nueva ? 'Listo: PDF generado' : 'Último PDF'} descripcion="Manda primero la portada por WhatsApp y luego el PDF.">
          <TarjetaVersion v={ultima} destacada />
        </Seccion>
      )}
      {/* Diego, 14-oct: recalculó COMPOMEX, bajó «el PDF» y le salió el de antes (la versión 1 seguía arriba con WhatsApp y
          Descargar, y «Generar otra versión» quedaba hasta abajo). Si el último PDF es de ANTES del último cálculo, primero va
          generar la versión nueva, y el viejo abajo, marcado. */}
      {vieja && <p className="aviso aviso--amarillo">{`El PDF de la versión ${ultima!.numero} tiene los números de antes del último cálculo. Genera la versión ${cot.version_actual + 1} aquí abajo antes de mandarlo.`}</p>}

      {puede('editar_cotizaciones') && (
        <Seccion titulo={ultima ? 'Generar otra versión' : 'Generar el PDF'} descripcion={`Formato ${ETIQUETA_FORMATO[formato]}${cot.version_actual > 0 ? ` · será la versión ${cot.version_actual + 1} (folio ${cot.folio}-V${cot.version_actual + 1})` : ` · folio ${cot.folio}`}`}>
          <div className="hoja pila">
            <ul className="lista-salida">
              {esAmpliacion ? (
                <Revision ok={Boolean(calculoPiezas)}>{calculoPiezas ? `Precio calculado · ${dinero(calculoPiezas.contado_con_iva)} contado con IVA` : 'Falta el precio: captura la batería, cuántas suma y los costos en «Ampliación».'}</Revision>
              ) : (
                <Revision ok={Boolean(b.resultado)}>{b.resultado ? `Opciones calculadas · recomendada ${ETIQUETA_OPCION[b.resultado.recomendada].toLowerCase()} ${cobra !== null ? `(${dinero(cobra)} contado)` : ''}` : 'Faltan las opciones: no hay datos para calcular.'}</Revision>
              )}
              <Revision ok={config.pdf.disponible}>{config.pdf.disponible ? 'Generador de la familia disponible' : `Generador no disponible: ${config.pdf.motivo ?? 'sin motivo'}`}</Revision>
              <Revision ok={formatoListo}>{formatoListo ? `Formato ${ETIQUETA_FORMATO[formato]} registrado en la plantilla` : `El formato "${ETIQUETA_FORMATO[formato]}" todavía no existe en la plantilla: el PDF va a fallar hasta que la familia lo publique.`}</Revision>
              {sinPdf && <Revision ok={false}>{sinPdf}</Revision>}
              {faltaTamano && <Revision ok={false}>{faltaTamano}</Revision>}
              {esAmpliacion ? (
                <>
                  {/* todos los que la detienen, cada uno con «Ver el dato» (lleva a su campo en «Ampliación») */}
                  {detienenAmpliacion.map((a) => (
                    <Revision key={a.id} ok={false}>
                      {a.mensaje}{' '}
                      {a.campo && (
                        <Enlace a={`/cotizaciones/${cot.id}/ampliacion?campo=${encodeURIComponent(a.campo)}`}>Ver el dato</Enlace>
                      )}
                    </Revision>
                  ))}
                  {sinGuardar && (
                    <Revision ok={false}>
                      {pendiente.cambios === 1 ? 'Tienes 1 cambio sin guardar' : pendiente.cambios > 1 ? `Tienes ${pendiente.cambios} cambios sin guardar` : 'Tienes cambios sin guardar'} en «Ampliación»: el PDF saldría con lo de
                      antes. Guárdalos (o descártalos) primero.{' '}
                      <Enlace a={`/cotizaciones/${cot.id}/ampliacion`}>Ir a guardarlos</Enlace>
                    </Revision>
                  )}
                  {noEngloba ? (
                    // qué pasa, en rojo, y el texto del aviso del servidor (MENSAJE_SIN_DESGLOSE: «… Ya se le avisó a Diego …»)
                    <Revision ok={false}>
                      <strong>La plantilla de PDF todavía no puede englobar el precio en uno solo.</strong> {plantilla.mensaje}
                    </Revision>
                  ) : (
                    <Revision ok>
                      {calculoPiezas?.msi_3 === null
                        ? `Lo que incluye y un solo precio (subtotal, IVA, total y anticipo; sin 3 MSI: la lista pasa de ${dinero(MSI_TOPE_LISTA_CON_IVA)}): el PDF no lleva el desglose por partida.`
                        : 'Lo que incluye y un solo precio (subtotal, IVA, total, anticipo y 3 MSI): el PDF no lleva el desglose por partida.'}
                    </Revision>
                  )}
                  {b.piezas?.hoja2 && plantilla !== null && !plantilla.hoja2 ? (
                    <Revision ok={false} aviso>
                      Sale sin hoja 2: la plantilla de PDF conectada todavía no tiene la hoja «cuánto te ayudan las baterías nuevas». El PDF sale de una hoja (la noche medida queda guardada en la cotización).
                    </Revision>
                  ) : b.piezas?.hoja2 ? (
                    <Revision ok>{`Con hoja 2: la noche del ${b.piezas.hoja2.noche.fecha} ${b.piezas.hoja2.noche.como}${b.piezas.hoja2.recarga ? '' : ' (sin recarga medida: no dice «llegas al amanecer»)'}`}</Revision>
                  ) : (
                    <Revision ok aviso>Sin hoja 2 (opcional): la puedes agregar en «Ampliación» con la noche medida en su monitoreo.</Revision>
                  )}
                </>
              ) : (
                sinViaticos && <Revision ok={false}>{sinViaticos}</Revision>
              )}
              {asesorListo !== null && (
                <Revision ok={asesorListo}>
                  {asesorListo && avisoAsesor
                    ? avisoAsesor.mensaje
                    : asesorListo
                    ? vendedorEsYo
                      ? 'Tu tarjeta de asesor está en la plantilla'
                      : 'La tarjeta del vendedor está en la plantilla'
                    : (avisoAsesor?.mensaje ?? 'No tienes tarjeta de asesor en la plantilla: pide a Diego darte de alta (foto y WhatsApp).')}
                  {/* punto o (Diego, 13-oct, 19:05): al director, el camino directo a donde se pone el WhatsApp */}
                  {!asesorListo && esDirector && (
                    <>
                      {' '}
                      <Enlace a="/configuracion/usuarios">Abrir Usuarios</Enlace>
                    </>
                  )}
                </Revision>
              )}
              {faltaOk && (
                <Revision ok aviso>
                  {soloFaltaMonto(avisosQueApruebaDiego(rojosPendientes(b.avisos)))
                    ? 'Falta el monto de un costo adicional: el PDF sale igual y Diego recibe el aviso.'
                    : 'El precio quedó abajo de lo recomendado: el PDF sale igual y Diego recibe el aviso.'}
                </Revision>
              )}
              {(arribaLey.length > 0 || arribaLeyMedida.length > 0) && <Revision ok={false}>{NO_SE_PUEDE_COTIZAR_LEY}</Revision>}
              {porArreglar.map((a) => (
                <Revision key={a.id} ok={false}>
                  {a.mensaje}
                </Revision>
              ))}
              {/* Verificación adversarial de la etapa 3: los rojos que no se confirman con nota también cuentan (antes decía "Sin
                  avisos rojos pendientes" con palomita junto al rojo de 700 kW, y el PDF no salía) */}
              <Revision ok={porArreglar.length === 0} aviso>
                {rojos.length === 0 && porArreglar.length === 0
                  ? 'Sin sugerencias pendientes'
                  : [
                      porArreglar.length ? `${porArreglar.length} ${porArreglar.length === 1 ? 'aviso rojo por arreglar' : 'avisos rojos por arreglar'} (no se confirma${porArreglar.length === 1 ? '' : 'n'} con nota: cambia lo que dice${porArreglar.length === 1 ? '' : 'n'})` : null,
                      rojos.length ? `${rojos.length} ${rojos.length === 1 ? 'sugerencia' : 'sugerencias'} arriba (no detienen el PDF)` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
              </Revision>
              {b.cuestionario.visita_tecnica_hecha ? null : <Revision ok aviso>Precotización: el PDF dice lo que puede cambiar con la visita técnica.</Revision>}
            </ul>

            <CampoTexto etiqueta="Motivo de esta versión (opcional)" maxLength={300} placeholder={esAmpliacion ? 'Ej. el proveedor confirmó el precio' : 'Ej. el cliente pidió quitar la holgada'} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
            <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
            <div className="fila-botones fila-botones--fin">
              {/* Ampliación: con cambios sin guardar, o con una plantilla que no engloba el precio, el botón no se ofrece (lo de arriba
                  dice por qué y qué hacer). */}
              {sinGuardar ? (
                <Enlace a={`/cotizaciones/${cot.id}/ampliacion`} className="boton boton--principal">
                  Guardar los cambios en «Ampliación»
                </Enlace>
              ) : noEngloba ? (
                <p className="nota-suave">El PDF de la ampliación sale cuando Diego conecte la plantilla nueva.</p>
              ) : (
                <Boton variante="principal" icono={<FileDown aria-hidden />} cargando={generando} disabled={sinPrecio || porArreglar.length > 0 || arribaLey.length > 0 || arribaLeyMedida.length > 0 || sinPdf !== null || faltaTamano !== null || sinViaticos !== null} onClick={() => void generar()}>
                  {generando ? 'Generando el PDF…' : 'Generar PDF'}
                </Boton>
              )}
            </div>
          </div>
        </Seccion>
      )}

      {(vieja ? conPdf : anteriores).length > 0 && (
        <Seccion titulo="Versiones anteriores">
          <div className="pila">
            {(vieja ? conPdf : anteriores).map((v) => (
              <TarjetaVersion key={v.numero} v={v} />
            ))}
          </div>
        </Seccion>
      )}
    </div>
  );
}
